import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ConfirmLogoutDialog from "./ConfirmLogoutDialog";
import ResiliationDialog from "./ResiliationDialog";
import "./AccountForm.css";
import Navbar from "./Navbar";
import TopHeader from "./TopHeader";
import TokenStore from "../../api/tokenStore";
import UserIdStore from "../../api/userIdStore";
import { apiCall } from "../../api/apiCall";
import generateToastContainer from "../../utils/ToastContainer";
import { useTranslation } from "../../i18n/UseTranslation";
import { useTheme } from "../../utils/ThemeContext";
import { useCurrentUser } from "../../expense_report/UserCurrentUser";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";

type Section = "account" | "security" | "subscription";

const SunIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const CopyIcon = ({ copied }: { copied: boolean }) => {
  if (copied) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#22c55e"
        strokeWidth="2"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }

  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />

      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
};

function AccountForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { t, language, setLanguage, availableLanguages } = useTranslation();

  const { theme, setTheme } = useTheme();
  const { isSuperAdmin } = useCurrentUser();

  const [activeSection, setActiveSection] = useState<Section>("account");

  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isResiliationOpen, setIsResiliationOpen] = useState(false);
  const [selectedResiliationUuid, setSelectedResiliationUuid] = useState<
    string | null
  >(null);
  const [isCopied, setIsCopied] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  const { data: me, isLoading } = useQuery({
    queryKey: ["fetchCurrentUser"],
    queryFn: () => apiCall<any>("/users/me"),
    enabled: isAuthReady && isAuthenticated,
  });

  const { data: allCompanies } = useQuery({
    queryKey: ["fetchAllCompanies"],
    queryFn: () => apiCall<any[]>("/companies", { method: "GET" }),
    enabled: isSuperAdmin && isAuthReady && isAuthenticated,
  });

  useEffect(() => {
    if (!me) return;

    setFirstName(me.first_name ?? me.firstName ?? "");

    setLastName(me.last_name ?? me.lastName ?? "");

    setPhone(me.phone_number ?? me.phoneNumber ?? "");
  }, [me]);

  const userSubscription =
    me?.Company?.Subscription ||
    me?.company?.Subscription ||
    me?.company?.subscription;

  const currentPlan =
    userSubscription?.status === "active" ? userSubscription.plan_code : "free";

  const userRole = me?.role?.name ?? (me?.Role as any)?.name ?? null;

  const canManageSubscription = ["admin", "super_admin"].includes(
    userRole ?? "",
  );

  const resiliationTargetUuid = isSuperAdmin
    ? selectedResiliationUuid
    : (me?.company_uuid ?? null);

  const resiliationTargetName = isSuperAdmin
    ? (allCompanies?.find((c: any) => c.uuid === selectedResiliationUuid)
        ?.name ?? "SUPPRIMER")
    : (me?.Company?.name ?? me?.company?.name ?? "SUPPRIMER");

  const userId = me?.firebaseUid ?? me?.firebase_uid;

  const updateMutation = useMutation({
    mutationFn: () =>
      apiCall(`/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          phone_number: phone,
        }),
      }),

    onSuccess: () => {
      toast.success(t.account.toastSuccess);

      queryClient.invalidateQueries({
        queryKey: ["fetchCurrentUser"],
      });
    },

    onError: (error: Error) => {
      toast.error(`${t.account.toastError}${error.message}`);
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () =>
      apiCall("/subscriptions/cancel", {
        method: "POST",
      }),

    onSuccess: () => {
      toast.success(t.subscription.cancelSuccess);

      queryClient.invalidateQueries({
        queryKey: ["fetchCurrentUser"],
      });

      setIsCancelDialogOpen(false);
    },

    onError: (error: Error) => {
      toast.error(`${t.subscription.cancelError}${error.message}`);
      setIsCancelDialogOpen(false);
    },
  });

  const resiliationMutation = useMutation({
    mutationFn: (deleteData: boolean) =>
      apiCall("/company/close-account", {
        method: "POST",
        body: JSON.stringify({
          delete_data: deleteData,
          ...(isSuperAdmin && resiliationTargetUuid
            ? { company_uuid: resiliationTargetUuid }
            : {}),
        }),
      }),

    onSuccess: () => {
      navigate("/");

      setTimeout(async () => {
        await queryClient.cancelQueries();
        queryClient.clear();
        TokenStore.clearToken();

        if (UserIdStore?.clearUserId) {
          UserIdStore.clearUserId();
        }

        try {
          const auth = getAuth();
          await signOut(auth);
        } catch {}
      }, 2500);
    },

    onError: (error: Error) => {
      toast.error(`Erreur lors de la résiliation : ${error.message}`);
      setIsResiliationOpen(false);
    },
  });

  const navItems: {
    id: Section;
    label: string;
  }[] = [
    {
      id: "account",
      label: t.account.myAccount,
    },
    {
      id: "security",
      label: t.account.security,
    },
    {
      id: "subscription",
      label: t.account.manageSubscription,
    },
  ];

  const openLogoutDialog = () => {
    setIsLogoutDialogOpen(true);
  };

  const closeLogoutDialog = () => {
    setIsLogoutDialogOpen(false);
  };

  const confirmLogout = async () => {
    setIsLogoutDialogOpen(false);
    navigate("/");

    setTimeout(async () => {
      await queryClient.cancelQueries();

      queryClient.clear();
      TokenStore.clearToken();

      if (UserIdStore?.clearUserId) {
        UserIdStore.clearUserId();
      }

      try {
        const auth = getAuth();
        await signOut(auth);
      } catch (error) {
        console.error("Erreur lors de la déconnexion Firebase :", error);
      }
    }, 100);
  };

  const openCancelDialog = () => {
    setIsCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  const confirmCancelSubscription = () => {
    cancelSubscriptionMutation.mutate();
  };

  const handleCopyEmail = async () => {
    if (!me?.email) return;

    await navigator.clipboard.writeText(me.email);

    setIsCopied(true);

    window.setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const renderAccountSection = () => {
    if (isLoading) {
      return <p className="account-loading">{t.common.loading}</p>;
    }

    return (
      <>
        <div className="account-fields-grid">
          <div className="account-field">
            <label className="account-field-label">{t.account.firstName}</label>

            <input
              className="account-field-input"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </div>

          <div className="account-field">
            <label className="account-field-label">{t.account.lastName}</label>

            <input
              className="account-field-input"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
        </div>

        <div className="account-field account-field--full">
          <label className="account-field-label">{t.account.myEmail}</label>

          <div className="account-email-row">
            <span className="account-email-value">{me?.email}</span>

            <button
              type="button"
              className="account-copy-btn"
              onClick={handleCopyEmail}
              title={t.common.copyEmail}
            >
              <CopyIcon copied={isCopied} />
            </button>
          </div>

          <span className="account-verified-badge">{t.account.verified}</span>
        </div>

        <div className="account-field account-field--full">
          <label className="account-field-label">{t.account.phone}</label>

          <input
            type="tel"
            className="account-field-input"
            value={phone}
            onChange={(event) => {
              const onlyNumbers = event.target.value.replace(/\D/g, "");
              setPhone(onlyNumbers);
            }}
            placeholder={t.account.phonePlaceholder || "0612345678"}
          />
        </div>

        <button
          type="button"
          className="account-save-btn"
          onClick={() => updateMutation.mutate()}
          disabled={updateMutation.isPending || !userId}
        >
          {updateMutation.isPending ? t.common.saving : t.common.save}
        </button>

        <hr className="account-preferences-divider" />

        <h3 className="account-preferences-title">{t.preferences.title}</h3>

        <div className="account-fields-grid account-preferences-grid">
          <div className="account-field">
            <label className="account-field-label">
              {t.preferences.language}
            </label>

            <div className="account-select-wrapper">
              <select
                className="account-select"
                value={language}
                onChange={(event) => setLanguage(event.target.value as any)}
              >
                {availableLanguages.map((availableLanguage) => (
                  <option
                    key={availableLanguage.code}
                    value={availableLanguage.code}
                  >
                    {availableLanguage.label}
                  </option>
                ))}
              </select>

              <svg
                className="account-select-chevron"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>

          <div className="account-field">
            <label className="account-field-label">{t.preferences.theme}</label>

            <div className="account-theme-toggle">
              <button
                type="button"
                className={`account-theme-btn ${
                  theme === "light" ? "active" : ""
                }`}
                onClick={() => setTheme("light")}
              >
                <SunIcon />
                {t.preferences.lightMode}
              </button>

              <button
                type="button"
                className={`account-theme-btn ${
                  theme === "dark" ? "active" : ""
                }`}
                onClick={() => setTheme("dark")}
              >
                <MoonIcon />
                {t.preferences.darkMode}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="dashboard-wrapper">
      <Navbar />
      <TopHeader />

      <div className="account-page">
        <div className="account-page-header">
          <h1 className="account-page-title">{t.account.pageTitle}</h1>
        </div>

        <div className="account-layout">
          <aside className="account-sidebar">
            <p className="account-sidebar-group-label">
              {t.account.sidebarLabel}
            </p>

            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`account-sidebar-item ${
                  activeSection === item.id ? "active" : ""
                }`}
                onClick={() => setActiveSection(item.id)}
              >
                {item.label}
              </button>
            ))}
          </aside>

          <main className="account-content">
            {activeSection === "account" && (
              <section className="account-section">
                <h2 className="account-section-title">{t.account.myProfile}</h2>

                {renderAccountSection()}
              </section>
            )}

            {activeSection === "security" && (
              <section className="account-section">
                <h2 className="account-section-title">{t.security.title}</h2>

                <p className="account-section-desc">{t.security.description}</p>

                {/* <div className="account-security-card">
                  <div>
                    <p className="account-security-label">
                      {t.security.password}
                    </p>

                    <p className="account-security-sub">
                      {t.security.lastModified}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="account-action-btn"
                  >
                    {t.common.edit}
                  </button>
                </div> */}

                <div className="account-danger-zone">
                  <h3 className="account-danger-title">
                    {t.security.dangerZone}
                  </h3>

                  <p className="account-section-desc">
                    {t.security.dangerDesc}
                  </p>

                  <button
                    type="button"
                    className="account-logout-btn"
                    onClick={openLogoutDialog}
                  >
                    {t.security.logout}
                  </button>
                </div>
              </section>
            )}

            {activeSection === "subscription" && (
              <section className="account-section">
                <h2 className="account-section-title">
                  {t.subscription.title}
                </h2>

                <p className="account-section-desc">
                  {t.subscription.description}
                </p>

                <div className="account-plan-card">
                  <div className="account-plan-info">
                    <span className="account-plan-badge">
                      {t.subscription.plans[
                        currentPlan as keyof typeof t.subscription.plans
                      ] ?? currentPlan}
                    </span>

                    <p className="account-plan-desc">
                      {currentPlan === "free"
                        ? t.subscription.freePlanDesc
                        : t.subscription.activePlanDesc}
                    </p>
                  </div>

                  {currentPlan === "free" && (
                    <button
                      type="button"
                      className="account-upgrade-btn"
                      onClick={() => navigate("/upgrade")}
                    >
                      {t.subscription.upgradeCta}
                    </button>
                  )}
                </div>

                {userSubscription && (
                  <div className="account-billing-info">
                    <div className="account-billing-row">
                      <span>{t.subscription.status}</span>

                      <span className="account-billing-value">
                        {userSubscription.status}
                      </span>
                    </div>

                    <div className="account-billing-row">
                      <span>{t.subscription.plan}</span>

                      <span className="account-billing-value">
                        {userSubscription.plan_code}
                      </span>
                    </div>

                    <div className="account-billing-row">
                      <span>{t.subscription.billingPeriod}</span>

                      <span className="account-billing-value">
                        {(() => {
                          const period =
                            userSubscription.billing_period ??
                            userSubscription.billingPeriod ??
                            userSubscription.interval;

                          if (period === "year" || period === "yearly") {
                            return t.subscription.yearlyBilling;
                          }
                          if (period === "month" || period === "monthly") {
                            return t.subscription.monthlyBilling;
                          }
                          return "—";
                        })()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Résiliation d'abonnement — admin/super_admin seulement */}
                {canManageSubscription &&
                  userSubscription &&
                  userSubscription.status === "active" &&
                  !userSubscription.cancel_at_period_end && (
                    <div className="account-danger-zone">
                      <h3 className="account-danger-title">
                        {t.subscription.cancelZoneTitle}
                      </h3>
                      <p className="account-section-desc">
                        {t.subscription.cancelZoneDesc}
                      </p>
                      <button
                        type="button"
                        className="account-logout-btn"
                        onClick={openCancelDialog}
                        disabled={cancelSubscriptionMutation.isPending}
                      >
                        {t.subscription.cancelCta}
                      </button>
                    </div>
                  )}

                {userSubscription?.cancel_at_period_end && (
                  <p className="account-section-desc">
                    {t.subscription.scheduledCancellation}
                  </p>
                )}

                {/* Quitter Notacito — admin/super_admin seulement */}
                {canManageSubscription && (
                  <div className="account-danger-zone account-danger-zone--resiliation">
                    <h3 className="account-danger-title">
                      {t.subscription.resiliationZoneTitle}
                    </h3>
                    <p className="account-section-desc">
                      {t.subscription.resiliationZoneDesc}
                    </p>

                    {isSuperAdmin && (
                      <div
                        className="account-field"
                        style={{ marginBottom: "1rem" }}
                      >
                        <label className="account-field-label">
                          {t.subscription.resiliationCompany}
                        </label>
                        <div className="account-select-wrapper">
                          <select
                            className="account-select"
                            value={selectedResiliationUuid ?? ""}
                            onChange={(e) =>
                              setSelectedResiliationUuid(e.target.value || null)
                            }
                          >
                            <option value="">
                              {t.subscription.chooseCompany}
                            </option>
                            {(allCompanies ?? [])
                              .filter((c: any) => !c.is_platform)
                              .map((c: any) => (
                                <option key={c.uuid} value={c.uuid}>
                                  {c.name}
                                </option>
                              ))}
                          </select>
                          <svg
                            className="account-select-chevron"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      className="account-logout-btn"
                      onClick={() => setIsResiliationOpen(true)}
                      disabled={
                        resiliationMutation.isPending ||
                        (isSuperAdmin && !selectedResiliationUuid)
                      }
                    >
                      {t.subscription.resiliationCta}
                    </button>
                  </div>
                )}
              </section>
            )}
          </main>
        </div>
      </div>
      <ConfirmLogoutDialog
        isOpen={isLogoutDialogOpen}
        title={t.common.logoutTitle}
        content={t.common.logoutContent}
        cancelText={t.common.cancel}
        confirmText={t.common.logoutConfirm}
        onCancel={closeLogoutDialog}
        onConfirm={confirmLogout}
      />

      <ConfirmLogoutDialog
        isOpen={isCancelDialogOpen}
        title={t.subscription.cancelConfirmTitle}
        content={t.subscription.cancelConfirmContent}
        cancelText={t.common.cancel}
        confirmText={t.subscription.cancelConfirmBtn}
        onCancel={closeCancelDialog}
        onConfirm={confirmCancelSubscription}
      />

      <ResiliationDialog
        isOpen={isResiliationOpen}
        companyName={resiliationTargetName}
        isPending={resiliationMutation.isPending}
        onCancel={() => setIsResiliationOpen(false)}
        onConfirm={(deleteData) => resiliationMutation.mutate(deleteData)}
        t={{
          cancel: t.common.cancel,
          confirm: t.common.confirm ?? "Confirmer",
          continue: t.common.continue ?? "Continuer",
          back: t.common.back ?? "Retour",
          ...t.resiliation,
        }}
      />

      {generateToastContainer()}
    </div>
  );
}

export default AccountForm;
